/**
 * EditorButton — the button for editor chrome (the page node's ⋯ menu
 * trigger, the canvas toolbar). A thin wrapper over Duro's `Button`, so call
 * sites pick a Duro `size` / `variant` and never pass raw styles; it adds the
 * two things editor chrome needs on top:
 *   • `title` → a Duro Tooltip (the hint a native `title` used to give);
 *   • `isolatePress` → the press stays on the button: its pointerdown and click
 *     don't bubble to the canvas / page node underneath (no node drag, no node
 *     click).
 *
 * holds-until: Duro ships a compact borderless and an icon-only Button (https://github.com/fredericrous/duro-design-system/issues/102); until then chrome buttons map to secondary/link small (bordered, 12px).
 */
import type { ReactNode, Ref } from "react";
import { html } from "react-strict-dom";
import {
  Button,
  Tooltip,
  type ButtonSize,
  type ButtonVariant,
} from "@duro-app/ui";

export type EditorButtonProps = {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly disabled?: boolean;
  readonly "aria-label"?: string;
  readonly "aria-expanded"?: boolean;
  /** Hover/focus hint, rendered as a Duro Tooltip. */
  readonly title?: string;
  /** Duro Button size. Default `small` (editor chrome is compact). */
  readonly size?: ButtonSize;
  /** Duro Button variant. Default `secondary`. */
  readonly variant?: ButtonVariant;
  /** Stop pointerdown and click from reaching the elements underneath. */
  readonly isolatePress?: boolean;
  /** Forwarded to the underlying <button> (React 19 ref-as-prop) — lets call
   *  sites measure it via useElementBounds for menu positioning. */
  readonly ref?: Ref<HTMLButtonElement>;
};

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

export function EditorButton({
  children,
  onClick,
  disabled,
  "aria-label": ariaLabel,
  "aria-expanded": ariaExpanded,
  title,
  size = "small",
  variant = "secondary",
  isolatePress = false,
  ref,
}: EditorButtonProps) {
  const button = (
    <Button
      ref={ref}
      size={size}
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
    >
      {children}
    </Button>
  );
  const pressed = isolatePress ? (
    <html.span onPointerDown={stop} onClick={stop}>
      {button}
    </html.span>
  ) : (
    button
  );
  if (!title) return pressed;
  return (
    <Tooltip.Root content={title}>
      <Tooltip.Trigger>{pressed}</Tooltip.Trigger>
    </Tooltip.Root>
  );
}
